import { useEffect, useMemo, useState } from "react";
import { getFeedback, createFeedback, replyFeedback, setFeedbackStatus, markFeedbackRead, getLeads } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { MessageSquarePlus, Send, ChevronDown } from "lucide-react";
import { toast } from "sonner";

export const FB_CATS = [
  { id: "vraag", label: "Vraag" }, { id: "suggestie", label: "Suggestie" },
  { id: "technisch", label: "Technisch probleem" }, { id: "overig", label: "Overig" },
];
export const FB_STATUS = { nieuw: "Nieuw", in_behandeling: "In behandeling", afgehandeld: "Afgehandeld" };
const STATUS_CLS = { nieuw: "bg-[#16BFBF] text-white", in_behandeling: "bg-[#FBF0DC] text-[#B7791F]", afgehandeld: "bg-[#E3E9F5] text-[#001C51]" };
const catLabel = (c) => (FB_CATS.find((x) => x.id === c) || {}).label || c;
const fmt = (s) => new Date(s).toLocaleString("nl-NL", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });

function NewItem({ onCreated }) {
  const [f, setF] = useState({ category: "vraag", subject: "", description: "", lead_id: "" });
  const [leads, setLeads] = useState([]);
  useEffect(() => { getLeads().then(setLeads).catch(() => {}); }, []);
  const submit = async () => {
    if (!f.subject.trim() || !f.description.trim()) return toast.error("Vul onderwerp en omschrijving in");
    try { await createFeedback({ ...f, lead_id: f.lead_id || null }); toast.success("Verstuurd — de admin ziet het direct"); setF({ category: "vraag", subject: "", description: "", lead_id: "" }); onCreated(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Versturen mislukt"); }
  };
  return (
    <div className="rounded-2xl border border-[#E2E7EC] bg-white p-5 space-y-3" data-testid="feedback-new">
      <h2 className="font-heading font-semibold text-lg text-[#16202B] flex items-center gap-2"><MessageSquarePlus className="h-5 w-5 text-[#16BFBF]" /> Nieuwe vraag of suggestie</h2>
      <div className="flex flex-wrap gap-1.5">
        {FB_CATS.map((c) => (
          <button key={c.id} data-testid={`fb-cat-${c.id}`} onClick={() => setF({ ...f, category: c.id })} className={`text-xs font-medium px-3 py-1.5 rounded-full border ${f.category === c.id ? "bg-[#001C51] text-white border-[#001C51]" : "border-[#E2E7EC] text-[#5B6675] hover:border-[#001C51]"}`}>{c.label}</button>
        ))}
      </div>
      <input data-testid="fb-subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className="fe-input" placeholder="Onderwerp (bijv. script verkoper, pakketprijs, belknop)" />
      <textarea data-testid="fb-description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={4} className="fe-input resize-y" placeholder="Omschrijf je vraag of voorstel…" />
      <select data-testid="fb-lead" value={f.lead_id} onChange={(e) => setF({ ...f, lead_id: e.target.value })} className="fe-input">
        <option value="">Geen lead koppelen (optioneel)</option>
        {leads.map((l) => <option key={l.id} value={l.id}>{l.name}{l.phone ? ` — ${l.phone}` : ""}</option>)}
      </select>
      <button data-testid="fb-submit" onClick={submit} className="w-full py-2.5 rounded-xl bg-[#001C51] text-white text-sm font-medium hover:bg-[#0A337A]">Versturen</button>
    </div>
  );
}

function Item({ it, isAdmin, onChanged }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const unread = isAdmin ? it.admin_unread : it.user_unread;
  const toggle = () => { setOpen(!open); if (!open && unread) markFeedbackRead(it.id).then(onChanged).catch(() => {}); };
  const send = async () => {
    if (!text.trim()) return;
    try { await replyFeedback(it.id, text); setText(""); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Versturen mislukt"); }
  };
  const status = async (s) => { try { await setFeedbackStatus(it.id, s); onChanged(); } catch { toast.error("Wijzigen mislukt"); } };
  return (
    <div data-testid={`fb-item-${it.id}`} className={`rounded-2xl border bg-white ${unread ? "border-[#16BFBF]" : "border-[#E2E7EC]"}`}>
      <button onClick={toggle} data-testid={`fb-toggle-${it.id}`} className="w-full text-left px-5 py-4 flex items-start gap-3">
        {unread && <span className="mt-1.5 h-2 w-2 rounded-full bg-[#16BFBF] shrink-0" data-testid={`fb-unread-${it.id}`} />}
        <div className="min-w-0">
          <p className="font-medium text-[#16202B]">{it.subject}</p>
          <p className="text-xs text-[#5B6675]">{catLabel(it.category)} · {isAdmin ? `${it.user_name || it.user_email} · ` : ""}{fmt(it.created_at)}{it.lead_name ? ` · lead: ${it.lead_name}` : ""} · {it.replies?.length || 0} reactie(s)</p>
        </div>
        <span data-testid={`fb-status-${it.id}`} className={`ml-auto text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${STATUS_CLS[it.status]}`}>{FB_STATUS[it.status]}</span>
        <ChevronDown className={`h-4 w-4 text-[#5B6675] shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-3">
          <p className="text-sm text-[#16202B] whitespace-pre-wrap rounded-xl bg-[#F6F9FB] p-3">{it.description}</p>
          {(it.replies || []).map((r, i) => (
            <div key={i} className={`rounded-xl p-3 text-sm ${r.by_role === "admin" ? "bg-[#EAFBFB] border border-[#D6F3F3]" : "bg-[#F6F9FB] border border-[#E2E7EC]"}`}>
              <p className="whitespace-pre-wrap text-[#16202B]">{r.text}</p>
              <p className="text-[11px] text-[#8994A3] mt-1">{r.by_name} {r.by_role === "admin" ? "(Admin)" : ""} · {fmt(r.at)}</p>
            </div>
          ))}
          <div className="flex gap-2">
            <textarea data-testid={`fb-reply-${it.id}`} value={text} onChange={(e) => setText(e.target.value)} rows={2} className="fe-input resize-y flex-1" placeholder={isAdmin ? "Antwoord aan medewerker…" : "Reageer…"} />
            <button data-testid={`fb-send-${it.id}`} onClick={send} className="self-end p-3 rounded-xl bg-[#001C51] text-white hover:bg-[#0A337A]"><Send className="h-4 w-4" /></button>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#5B6675]">Status:</span>
              {Object.entries(FB_STATUS).map(([k, v]) => (
                <button key={k} data-testid={`fb-set-${k}-${it.id}`} onClick={() => status(k)} className={`text-xs px-3 py-1.5 rounded-full border ${it.status === k ? "bg-[#001C51] text-white border-[#001C51]" : "border-[#E2E7EC] text-[#5B6675] hover:border-[#001C51]"}`}>{v}</button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Vragen() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("open");
  const load = () => getFeedback().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const shown = useMemo(() => (items || []).filter((i) => filter === "all" || (filter === "open" ? i.status !== "afgehandeld" : i.status === "afgehandeld")), [items, filter]);

  return (
    <div className="px-4 lg:px-8 py-6 max-w-6xl mx-auto fe-fade-up" data-testid="vragen-page">
      <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#16202B]">Vragen & suggesties</h1>
      <p className="text-[#5B6675] mt-1 mb-6 text-sm">{isAdmin ? "Alle vragen en voorstellen van medewerkers. Reageer en werk de status bij." : "Stel een vraag of doe een voorstel. Je ziet hier ook de antwoorden."}</p>
      <div className={`grid gap-6 ${isAdmin ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-5"}`}>
        {!isAdmin && <div className="lg:col-span-2"><NewItem onCreated={load} /></div>}
        <div className={isAdmin ? "" : "lg:col-span-3"}>
          <div className="inline-flex rounded-xl border border-[#E2E7EC] bg-white p-1 mb-4">
            {[["open", "Open"], ["done", "Afgehandeld"], ["all", "Alles"]].map(([k, v]) => (
              <button key={k} data-testid={`fb-filter-${k}`} onClick={() => setFilter(k)} className={`px-4 py-2 rounded-lg text-sm font-medium ${filter === k ? "bg-[#001C51] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"}`}>{v}</button>
            ))}
          </div>
          {items === null ? <p className="text-sm text-[#5B6675]">Laden…</p>
            : shown.length === 0 ? <p className="text-sm text-[#5B6675]" data-testid="fb-empty">Geen items.</p>
            : <div className="space-y-3">{shown.map((it) => <Item key={it.id} it={it} isAdmin={isAdmin} onChanged={load} />)}</div>}
        </div>
      </div>
    </div>
  );
}
